import { useState } from "react";
import { Bell, CalendarCheck2, Loader2, MapPin, Search, Ticket } from "lucide-react";
import { Reveal, Eyebrow } from "./Reveal";
import { scrollToSection } from "../lib/scroll";

const CLINIC_PHOTO =
  "https://images.unsplash.com/photo-1643660526741-094639fbe53a?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1OTV8MHwxfHNlYXJjaHwyfHxtb2Rlcm4lMjBkZW50YWwlMjBjbGluaWMlMjBpbnRlcmlvciUyMGRlbnRpc3QlMjBwYXRpZW50fGVufDB8fHx8MTc5MTM4OTE0NHww&ixlib=rb-4.1.0&q=85";

const demoClinics = [
  { name: "Bright Smile Dental", meta: "General dentistry · 0.6 mi" },
  { name: "Hudson Dental Group", meta: "Orthodontics · 1.2 mi" },
  { name: "Madison Ave Dental", meta: "Cleanings & check-ups · 1.8 mi" },
];

const CellShell = ({ children, className = "" }) => (
  <div
    className={`flex h-full flex-col overflow-hidden rounded-3xl border border-line bg-white shadow-soft ${className}`}
  >
    {children}
  </div>
);

const FinderDemo = () => {
  const [query, setQuery] = useState("New York, NY");
  const [searching, setSearching] = useState(false);
  const [results, setResults] = useState(null);

  const runSearch = () => {
    setSearching(true);
    setResults(null);
    setTimeout(() => {
      setResults(demoClinics);
      setSearching(false);
    }, 700);
  };

  return (
    <div className="mt-6">
      <div className="flex gap-2">
        <div className="flex flex-1 items-center gap-2 rounded-full border border-line bg-paper px-4 py-3">
          <MapPin className="h-4 w-4 shrink-0 text-brand" />
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && runSearch()}
            placeholder="City, state or ZIP"
            data-testid="clinic-search-input"
            className="w-full bg-transparent text-sm font-medium text-ink outline-none placeholder:text-ink/35"
          />
        </div>
        <button
          onClick={runSearch}
          data-testid="clinic-search-button"
          className="inline-flex items-center gap-2 rounded-full bg-brand px-5 py-3 text-sm font-semibold text-white transition-[transform,background-color] duration-200 hover:-translate-y-0.5 hover:bg-brand-deep"
        >
          {searching ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
          Search
        </button>
      </div>
      <div className="mt-4 space-y-2">
        {results === null && !searching && (
          <p className="rounded-2xl border border-dashed border-line px-4 py-4 text-sm text-ink/40">
            Try the search — a demo preview of the in-app clinic finder.
          </p>
        )}
        {searching && (
          <p className="rounded-2xl bg-mint-soft px-4 py-4 text-sm font-medium text-brand-deep">
            Finding clinics near you…
          </p>
        )}
        {results?.map((c, i) => (
          <div
            key={c.name}
            className="flex items-center justify-between rounded-2xl border border-line px-4 py-3"
          >
            <div>
              <p className="text-sm font-semibold text-ink">{c.name}</p>
              <p className="text-xs text-ink/45">{c.meta}</p>
            </div>
            <CalendarCheck2 className="h-4 w-4 text-brand" />
          </div>
        ))}
        {results && (
          <p className="pt-1 font-mono text-[10px] uppercase tracking-[0.18em] text-ink/35">
            Demo preview — live clinic data appears in the app
          </p>
        )}
      </div>
    </div>
  );
};

export const About = () => (
  <section id="about" className="relative py-24 lg:py-32" data-testid="about-section">
    <div className="mx-auto max-w-7xl px-5 sm:px-8">
      <div className="max-w-2xl">
        <Reveal>
          <Eyebrow>About the app</Eyebrow>
        </Reveal>
        <Reveal delay={0.08}>
          <h2 className="mt-4 font-display text-4xl font-medium leading-[1.08] tracking-tight sm:text-5xl">
            Everything your smile needs, <em className="italic text-brand">in one app.</em>
          </h2>
        </Reveal>
        <Reveal delay={0.16}>
          <p className="mt-5 text-lg leading-relaxed text-ink/60">
            AssisDent connects you to thousands of dental clinics across the USA — find a clinic,
            book a visit and save on your first appointment.
          </p>
        </Reveal>
      </div>

      <Reveal delay={0.2}>
        <div
          className="mt-10 grid gap-8 rounded-3xl border border-line bg-white p-7 shadow-soft sm:p-9 md:grid-cols-5"
          data-testid="what-we-do"
        >
          <div className="md:col-span-3">
            <Eyebrow>What we do</Eyebrow>
            <h3 className="mt-3 font-display text-2xl font-medium sm:text-3xl">
              A portal for dental services — built on a nationwide clinic network.
            </h3>
            <p className="mt-4 text-base leading-relaxed text-ink/60">
              AssisDent connects patients with 5,000+ dental clinics across the USA. You can find a
              clinic near you, book an appointment in a few clicks, and get a discount on your
              first booking. Fill in the application below and our team will call you back to
              confirm.
            </p>
            <button
              onClick={() => scrollToSection("register")}
              data-testid="what-we-do-book-button"
              className="mt-6 inline-flex items-center gap-2 rounded-full bg-ink px-6 py-3 text-sm font-semibold text-paper transition-[transform,background-color] duration-200 hover:-translate-y-0.5 hover:bg-brand-deep"
            >
              Book now
            </button>
          </div>
          <div className="space-y-3 md:col-span-2">
            {[
              { icon: <MapPin className="h-4 w-4" />, text: "5,000+ clinics across the USA" },
              { icon: <CalendarCheck2 className="h-4 w-4" />, text: "Appointments booked in the app" },
              { icon: <Ticket className="h-4 w-4" />, text: "Discount on your first booking" },
            ].map((row) => (
              <div
                key={row.text}
                className="flex items-center gap-3 rounded-2xl border border-line bg-paper px-4 py-3.5"
              >
                <span className="grid h-8 w-8 shrink-0 place-items-center rounded-xl bg-mint-soft text-brand-deep">
                  {row.icon}
                </span>
                <p className="text-sm font-semibold text-ink/80">{row.text}</p>
              </div>
            ))}
          </div>
        </div>
      </Reveal>

      <div className="mt-14 grid gap-5 md:grid-cols-3">
        <Reveal className="md:col-span-2" delay={0.05}>
          <CellShell className="relative min-h-[320px]">
            <img
              src={CLINIC_PHOTO}
              alt="Dental clinic treatment room"
              className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 hover:scale-[1.03]"
            />
            <div
              className="absolute inset-0"
              style={{ background: "linear-gradient(180deg, rgba(11,42,38,0) 30%, rgba(11,42,38,0.82) 100%)" }}
            />
            <div className="relative z-10 mt-auto flex items-end justify-between gap-4 p-7 pt-40 text-paper sm:p-9 sm:pt-56">
              <div>
                <p className="font-mono text-[11px] font-semibold uppercase tracking-[0.24em] text-brand-bright">
                  The network
                </p>
                <p className="mt-2 font-display text-3xl font-medium sm:text-4xl" data-testid="stat-clinics">
                  5,000+ clinics across the USA
                </p>
              </div>
              <span className="hidden shrink-0 rounded-full border border-paper/25 px-4 py-2 font-mono text-[11px] text-paper/80 sm:block">
                nationwide
              </span>
            </div>
          </CellShell>
        </Reveal>

        <Reveal delay={0.12}>
          <CellShell className="bg-gold-bg/60 border-gold-border/70">
            <div className="p-7 sm:p-8">
              <span className="grid h-11 w-11 place-items-center rounded-2xl bg-gold-bg text-gold">
                <Ticket className="h-5 w-5" />
              </span>
              <h3 className="mt-5 font-display text-2xl font-medium">First-visit discount</h3>
              <p className="mt-3 text-sm leading-relaxed text-ink/60">
                Your first booking in AssisDent comes with a discount — applied automatically when
                you choose a clinic and confirm a time.
              </p>
              <button
                onClick={() => scrollToSection("register")}
                data-testid="claim-discount-button"
                className="mt-6 inline-flex items-center gap-2 rounded-full border border-ink/15 bg-white px-5 py-2.5 text-sm font-semibold text-ink transition-[transform,border-color,background-color] duration-200 hover:-translate-y-0.5 hover:border-gold hover:bg-white"
              >
                Book with your discount
              </button>
            </div>
          </CellShell>
        </Reveal>

        <Reveal className="md:col-span-2" delay={0.05}>
          <CellShell>
            <div className="p-7 sm:p-8">
              <span className="grid h-11 w-11 place-items-center rounded-2xl bg-mint-soft text-brand-deep">
                <MapPin className="h-5 w-5" />
              </span>
              <h3 className="mt-5 font-display text-2xl font-medium">Find a clinic near you</h3>
              <p className="mt-2 text-sm leading-relaxed text-ink/60">
                Search by city, state or ZIP — the app sorts clinics by distance so the nearest
                opening is never far away.
              </p>
              <FinderDemo />
            </div>
          </CellShell>
        </Reveal>

        <Reveal delay={0.12}>
          <CellShell>
            <div className="p-7 sm:p-8">
              <span className="grid h-11 w-11 place-items-center rounded-2xl bg-mint-soft text-brand-deep">
                <Bell className="h-5 w-5" />
              </span>
              <h3 className="mt-5 font-display text-2xl font-medium">Never miss a visit</h3>
              <p className="mt-3 text-sm leading-relaxed text-ink/60">
                Confirmations and reminders live in the app, so your next appointment is always one
                glance away.
              </p>
              <div className="mt-6 space-y-2">
                <div className="flex items-center gap-3 rounded-2xl border border-line bg-paper px-4 py-3">
                  <span className="h-2 w-2 shrink-0 rounded-full bg-brand" />
                  <p className="text-xs font-medium text-ink/70">Booked · Dental check-up, 9:40</p>
                </div>
                <div className="flex items-center gap-3 rounded-2xl border border-line bg-paper px-4 py-3">
                  <span className="h-2 w-2 shrink-0 rounded-full bg-gold" />
                  <p className="text-xs font-medium text-ink/70">Reminder · Tomorrow, 9:00</p>
                </div>
              </div>
            </div>
          </CellShell>
        </Reveal>
      </div>
    </div>
  </section>
);
